import { Component, Input, OnInit } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { SeriesColumnOptions } from 'highcharts';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-live-chart',
    templateUrl: './live-chart.component.html',
    styleUrls: ['./live-chart.component.scss'],
})
export class LiveChartComponent implements OnInit {
    @Input() isDetail = false;

    chart: Chart = null;
    _unit = 'Watt';

    constructor(private translate: TranslateService) {}

    ngOnInit() {
        this.initializeChart();
    }

    setUnit(unit: string): void {
        this._unit = unit;
    }

    addNewSeries(
        values,
        y_attribute: string,
        remove_series_count = 0,
        options?: AddNewLiveChartSeriesOptions
    ): void {
        if (remove_series_count >= 0) {
            this.removeSeries(0, remove_series_count);
        }

        if ('results' in values) {
            values = values.results;
        }

        const s: SeriesColumnOptions = {
            name: null,
            zIndex: 1,
            data: values.map((element) => {
                return [new Date(element.timestamp).getTime(), element[y_attribute]];
            }),
            type: 'column',
            edgeWidth: 0,
            borderWidth: 0,
            pointPadding: 0,
        };

        if (options?.zindex) {
            s.zIndex = options.zindex;
        }

        if (options?.color) {
            s.color = options.color;
        }

        if (this.chart) {
            this.chart.addSeries(s, true, false);
        }
    }

    removeSeries(idx: number, count = -1): void {
        if (count === -1) {
            this.chart.removeSeries(idx);
        } else {
            for (let i = idx; i < idx + count; ++i) {
                if (this.chart) {
                    this.chart.removeSeries(i);
                }
            }
        }
    }

    updateZoomLevel(resolution: number, date_format: string): void {
        this.chart.ref.update(
            {
                xAxis: {
                    tickInterval: resolution,
                },
                tooltip: {
                    xDateFormat: date_format,
                },
                series: [
                    {
                        name: null,
                        data: [],
                        type: 'column',
                    },
                ],
            },
            true
        );
    }

    reset(): void {
        if (!this.chart.ref) {
            return;
        }
        const numSeries = this.chart.ref.series.length + 1;
        for (let i = 0; i < numSeries; ++i) {
            this.chart.removeSeries(i);
        }
        this.chart.ref.redraw(false);
        this.showLoadingState(true);
    }

    showLoadingState(show: boolean = true): void {
        if (show) {
            this.chart.ref.showLoading(
                this.translate.instant('screens.dashboard.charts.liveChart.loading.noData')
            );
        } else {
            if (this.chart) {
                if (this.chart.ref) {
                    this.chart.ref.hideLoading();
                }
            }
        }
    }

    private initializeChart(): void {
        const self = this;

        this.chart = new Chart({
            chart: {
                type: 'column',
                backgroundColor: 'rgba(255, 255, 255, 0)',
                margin: [50, 0, 50, 40],
            },
            title: {
                text: null,
            },
            xAxis: {
                title: {
                    text: self.translate.instant('screens.dashboard.live.time'),
                    align: 'low',
                    x: -32,
                    y: 0,
                    style: {
                        fontSize: '13px',
                        fontFamily: 'EONBrixSans, sans-serif',
                    },
                },
                type: 'datetime',
                showFirstLabel: !this.isDetail,
                showLastLabel: !this.isDetail,
                tickAmount: this.isDetail ? 2 : null,
                tickInterval: 1000,
                dateTimeLabelFormats: {
                    millisecond: '%H:%M',
                    second: '%H:%M',
                    minute: '%H:%M',
                    hour: '%H:%M',
                    day: '%d.%m',
                    week: '%d.%m',
                    month: '%m.%Y',
                    year: '%Y',
                },
                labels: {
                    enabled: true,
                    // align: 'middle',
                    style: {
                        fontSize: '13px',
                        fontFamily: 'EONBrixSans, sans-serif',
                        fontWeight: 'bold',
                    },
                },
                crosshair: {
                    width: 1,
                    color: 'rgba(0,0,0,0.6)',
                    zIndex: 3,
                },
                minPadding: 0,
                maxPadding: 0,
            },
            yAxis: {
                title: {
                    text: 'Watt',
                    align: 'high',
                    offset: 0,
                    rotation: 0,
                    y: -20,
                    x: -8,
                    style: {
                        fontFamily: 'EONBrixSans, sans-serif',
                    },
                },
                startOnTick: this.isDetail,
                tickAmount: 4,
                labels: {
                    align: 'right',
                    x: -8,
                    y: 6,
                    step: 1,
                    formatter() {
                        if (this.value >= 1) {
                            return this.value.toLocaleString('de-DE');
                        } else {
                            return null;
                        }
                    },
                    style: {
                        fontSize: '13px',
                        fontFamily: 'EONBrixSans, sans-serif',
                        fontWeight: 'bold',
                    },
                },
                gridZIndex: 10,
                gridLineColor: 'rgba(0,0,0,0.20)',
            },
            tooltip: {
                xDateFormat: `%H:%M:%S ${self.translate.instant('screens.dashboard.liveDetailZoomLevels.timeUnit')}`,
                hideDelay: 0,
                animation: false,
                shadow: true,
                padding: 0,
                positioner(boxWidth: number, boxHeight: number, point: any) {
                    if (point.plotX + this.chart.plotLeft + boxWidth > this.chart.plotWidth) {
                        return {
                            x: point.plotX,
                            y: point.plotY + boxHeight / 2,
                        };
                    } else {
                        return {
                            x: point.plotX,
                            y: point.plotY + boxHeight / 2,
                        };
                    }
                },
                useHTML: true,
                formatter() {
                    const d = moment(this.key).format('HH:mm:ss');
                    const style = `style="border-color: ${this.color}"`;
                    const name = `<div class="header">${d} ${self.translate.instant('screens.dashboard.liveDetailZoomLevels.timeUnit')}</div>`;
                    const value = `<div class="body"> ${Math.floor(this.y).toLocaleString('de-DE')} ${self._unit}</div>`;
                    return `<div class="column-callout" ${style}> ${name} ${value}</div>`;
                },
                backgroundColor: 'transparent',
                borderWidth: 0,
                borderRadius: 0,
            },
            plotOptions: {
                series: {
                    showInLegend: false,
                    animation: false,
                    marker: {
                        states: {
                            hover: {
                                enabled: false,
                            },
                        },
                    },
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                },
                column: {
                    pointPadding: 0,
                    groupPadding: 0,
                    zoneAxis: 'x',
                    borderWidth: 0,
                    grouping: false,
                    states: {
                        hover: {
                            enabled: false,
                        },
                        inactive: {
                            enabled: false,
                        },
                    },
                    edgeWidth: 0,
                },
            },
            series: [],
            credits: {
                enabled: false,
            },
        });
    }
}

export interface AddNewLiveChartSeriesOptions {
    zindex?: number;
    isTileChart?: boolean;
    color?: string;
}
